import { mediaTranscriptApi } from "@circle-react/api/mediaTranscriptApi";
import type { MediaTranscript } from "@circle-react/types/MediaTranscript";
import { useMutation, useQuery } from "react-query";

import { t } from "@/i18n-js/instance";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";
import type { ApiError } from "@/react/config/CustomErrors";

import { useParsedWebVTTFile } from "./useParsedWebVTTFile";

export const useMediaTranscript = (mediaTranscriptId: number | null) => {
  const { success, error } = useToast();
  const {
    data: mediaTranscript,
    isInitialLoading: isLoading,
    refetch,
  } = useQuery<MediaTranscript, ApiError>({
    queryKey: ["mediaTranscript", mediaTranscriptId],
    queryFn: () => mediaTranscriptApi.show(mediaTranscriptId ?? -1),
    enabled: !!mediaTranscriptId,
  });
  const {
    webvtt,
    parsedWebvtt,
    refetch: refetchWebvtt,
  } = useParsedWebVTTFile(mediaTranscript) || {};
  const {
    mutateAsync: updateAsync,
    mutate: update,
    isPending: isUpdating,
  } = useMutation({
    mutationFn: formData =>
      mediaTranscriptApi.update(mediaTranscriptId ?? -1, formData),
    onSuccess: () => {
      success(t("media_player.transcript_menu.success"));
      void refetch();
      void refetchWebvtt();
    },
    onError: responseError => {
      error(t("media_player.transcript_menu.error"));
      console.error(responseError);
    },
  });
  const canEdit = mediaTranscript?.can_edit;

  return {
    mediaTranscript,
    canEdit,
    isLoading,
    isUpdating,
    webvtt,
    parsedWebvtt,
    update,
    updateAsync,
    refetch,
  };
};
